import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for robust user location detection with multiple fallback methods
 * @param {Object} options - Configuration options
 * @param {boolean} options.autoStart - Automatically attempt to get location on mount
 * @param {boolean} options.enableConsoleLogging - Enable console logging for debugging
 * @returns {Object} Location state and methods
 */
export const useUserLocation = (options = {}) => {
  const { autoStart = false, enableConsoleLogging = false } = options;

  // State
  const [userLocation, setUserLocation] = useState(null);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [locationAttempts, setLocationAttempts] = useState(0);

  // Refs to track state and prevent multiple requests
  const hasAutoStarted = useRef(false);
  const isGettingLocationRef = useRef(false);

  // Method 1: GPS-based location
  // options.highAccuracy: when true, prefer fresh fix (maxAge 0) and longer timeout for better accuracy
  const getUserLocationViaGPS = (options = {}) => {
    const { highAccuracy = false } = options;
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          resolve({ lat: latitude, lng: longitude, method: 'GPS' });
        },
        (error) => {
          let errorMsg = 'Unknown GPS error';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMsg = 'Location access denied by user';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMsg = 'Location information unavailable';
              break;
            case error.TIMEOUT:
              errorMsg = 'Location request timed out';
              break;
          }
          reject(new Error(errorMsg));
        },
        {
          enableHighAccuracy: true,
          timeout: highAccuracy ? 15000 : 8000,
          maximumAge: highAccuracy ? 0 : 300000, // 0 = no cache when highAccuracy, else 5 min
        }
      );
    });
  };

  // Method 2: IP-based location fallback
  const getUserLocationViaIP = () => {
    return new Promise((resolve, reject) => {
      const fetchIPLocation = async () => {
        try {
          // Try multiple IP location services for better reliability
          const services = [
            {
              name: 'ip-api.com',
              url: 'http://ip-api.com/json/',
              parser: (data) => ({
                lat: parseFloat(data.lat),
                lng: parseFloat(data.lon),
                method: 'IP',
                city: data.city,
                country: data.country,
                region: data.regionName,
                timezone: data.timezone,
                isp: data.isp,
              }),
            },
            {
              name: 'ipapi.co',
              url: 'https://ipapi.co/json/',
              parser: (data) => ({
                lat: parseFloat(data.latitude),
                lng: parseFloat(data.longitude),
                method: 'IP',
                city: data.city,
                country: data.country_name,
                region: data.region,
                timezone: data.timezone,
                isp: data.org,
              }),
            },
            {
              name: 'geojs.io',
              url: 'https://get.geojs.io/v1/ip/geo.json',
              parser: (data) => ({
                lat: parseFloat(data.latitude),
                lng: parseFloat(data.longitude),
                method: 'IP',
                city: data.city,
                country: data.country,
                region: data.region,
                timezone: data.timezone,
                isp: data.organization_name,
              }),
            },
          ];

          let lastError = null;

          for (const service of services) {
            try {
              const response = await fetch(service.url);

              if (!response.ok) {
                throw new Error(
                  `HTTP ${response.status}: ${response.statusText}`
                );
              }

              const data = await response.json();

              const location = service.parser(data);

              if (location.lat && location.lng) {
                resolve(location);
                return;
              } else {
                throw new Error('Invalid coordinates in response');
              }
            } catch (error) {
              lastError = error;
              continue;
            }
          }

          // If all services failed
          reject(
            new Error(
              `All IP location services failed. Last error: ${
                lastError?.message || 'Unknown error'
              }`
            )
          );
        } catch (error) {
          reject(new Error('IP location service failed'));
        }
      };

      fetchIPLocation();
    });
  };

  // Method 3: Browser's timezone-based approximation fallback
  const getUserLocationViaTimezone = () => {
    return new Promise((resolve, reject) => {
      try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        // Simple timezone to approximate coordinates mapping
        const timezoneCoords = {
          'America/New_York': { lat: 40.7128, lng: -74.006 },
          'America/Chicago': { lat: 41.8781, lng: -87.6298 },
          'America/Denver': { lat: 39.7392, lng: -104.9903 },
          'America/Los_Angeles': { lat: 34.0522, lng: -118.2437 },
          'America/Phoenix': { lat: 33.4484, lng: -112.074 },
          'Europe/London': { lat: 51.5074, lng: -0.1278 },
          'Europe/Paris': { lat: 48.8566, lng: 2.3522 },
          'Europe/Berlin': { lat: 52.52, lng: 13.405 },
          'Asia/Tokyo': { lat: 35.6762, lng: 139.6503 },
          'Asia/Shanghai': { lat: 31.2304, lng: 121.4737 },
        };

        const coords = timezoneCoords[timezone];
        if (coords) {
          resolve({ ...coords, method: 'Timezone', timezone });
        } else {
          // Default to central US if timezone not found
          resolve({ lat: 39.8283, lng: -98.5795, method: 'Default', timezone });
        }
      } catch (error) {
        reject(new Error('Timezone detection failed'));
      }
    });
  };

  // Method 4: Manual address geocoding
  const geocodeAddress = useCallback(async (address) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          address
        )}&limit=1`
      );
      const data = await response.json();

      if (data && data.length > 0) {
        const result = data[0];
        return {
          lat: parseFloat(result.lat),
          lng: parseFloat(result.lon),
          method: 'Manual',
          address: result.display_name,
        };
      } else {
        throw new Error('Address not found');
      }
    } catch (error) {
      throw new Error('Geocoding service failed');
    }
  }, []);

  // Main location detection function with fallbacks
  const getLocationWithFallbacks = useCallback(async () => {
    // Prevent multiple simultaneous requests
    if (isGettingLocation || isGettingLocationRef.current) return;

    isGettingLocationRef.current = true;
    setIsGettingLocation(true);
    setLocationError(null);
    setLocationAttempts((prev) => prev + 1);

    const methods = [
      { name: 'GPS', func: getUserLocationViaGPS },
      { name: 'IP', func: getUserLocationViaIP },
      { name: 'Timezone', func: getUserLocationViaTimezone },
    ];

    for (const method of methods) {
      try {
        const location = await method.func();

        setUserLocation(location);
        setLocationError(null);
        setIsGettingLocation(false);
        isGettingLocationRef.current = false;

        return location;
      } catch (error) {
        // When GPS permission is denied, do not fall back to IP/timezone (wrong address)
        const isPermissionDenied =
          method.name === 'GPS' &&
          (error?.message?.toLowerCase().includes('denied') ||
            error?.code === 1);
        if (isPermissionDenied) {
          setLocationError('Location access denied by user');
          setIsGettingLocation(false);
          isGettingLocationRef.current = false;
          return { __permissionDenied: true };
        }
        continue;
      }
    }

    // If all methods fail
    setLocationError('Could not determine your location');
    setIsGettingLocation(false);
    isGettingLocationRef.current = false;
  }, [isGettingLocation]);

  // Set location from manual address input
  const setLocationFromAddress = useCallback(
    async (address) => {
      if (!address.trim()) {
        setLocationError('Please enter a valid address');
        return false;
      }

      // Prevent multiple simultaneous requests
      if (isGettingLocation || isGettingLocationRef.current) {
        return false;
      }

      isGettingLocationRef.current = true;
      setIsGettingLocation(true);
      setLocationError(null);

      try {
        const location = await geocodeAddress(address);
        setUserLocation(location);
        setLocationError(null);

        return location;
      } catch (error) {
        const errorMsg = `Could not find address: ${address}`;
        setLocationError(errorMsg);

        return false;
      } finally {
        setIsGettingLocation(false);
        isGettingLocationRef.current = false;
      }
    },
    [geocodeAddress, isGettingLocation]
  );

  // Clear location and error state
  const clearLocation = useCallback(() => {
    setUserLocation(null);
    setLocationError(null);
    setLocationAttempts(0);
    isGettingLocationRef.current = false;
    hasAutoStarted.current = false;
  }, []);

  // Auto-attempt to get location on component mount if enabled
  useEffect(() => {
    if (
      autoStart &&
      !hasAutoStarted.current &&
      !userLocation &&
      !isGettingLocation
    ) {
      hasAutoStarted.current = true;
      // Use a timeout to avoid calling getLocationWithFallbacks in the render cycle
      const timeoutId = setTimeout(() => {
        getLocationWithFallbacks();
      }, 0);

      return () => clearTimeout(timeoutId);
    }
  }, [autoStart, userLocation, isGettingLocation, getLocationWithFallbacks]);

  return {
    // State
    userLocation,
    isGettingLocation,
    locationError,
    locationAttempts,

    // Methods
    getLocationWithFallbacks,
    setLocationFromAddress,
    clearLocation,

    // Individual location methods (for advanced usage)
    getUserLocationViaGPS,
    getUserLocationViaIP,
    getUserLocationViaTimezone,
    geocodeAddress,
  };
};

export default useUserLocation;
